import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import styles from "../Create/add-site.module.css";
import { Input } from "../../../components/Shared/Forms";
import { PhoneInput } from "react-international-phone";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useSiteContext } from "../../../context/siteContext";
import { useToast } from "../../../context/ToastContext";
import { Typography } from "@mui/joy";
import ButtonPanel from "../../../components/Shared/ButtonPanel";
import { Button } from "primereact/button";
import useApiService from "../../../hooks/useApiService";
import { Site } from "../../../models/site.interface";
import { capitaliseFirstLetter, snakeToHuman } from "../../../utils/generalUtils";

type formDataType = {
  client_safety_agent_company_name?: string;
  client_safety_agent_name?: string;
  client_safety_agent_phone?: string;
  client_safety_agent_email?: string;
  client_architect_company_name?: string;
  client_architect_name?: string;
  client_architect_phone?: string;
  client_architect_email?: string;
  client_structural_engineer_company_name?: string;
  client_structural_engineer_phone?: string;
  client_structural_engineer_email?: string;
  client_structural_engineer_name?: string;
};

export default function SiteProfessionalTeam() {
  const { site, setSite } = useSiteContext();
  const toast = useToast();
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [somethingChanged, setSomethingChanged] = useState<boolean>(false);
  const [formData, setFormData] = useState<formDataType>({
    client_safety_agent_company_name: "",
    client_safety_agent_name: "",
    client_safety_agent_phone: "",
    client_safety_agent_email: "",
    client_architect_company_name: "",
    client_architect_name: "",
    client_architect_phone: "",
    client_architect_email: "",
    client_structural_engineer_company_name: "",
    client_structural_engineer_phone: "",
    client_structural_engineer_email: "",
    client_structural_engineer_name: "",
  });

  const { loading: updatingSite, fetchData: updateSite } = useApiService<Site>({
    fetchOnMount: false,
    url: `/site-list/${site?.id}`,
    method: "PUT",
    onSuccess: data => {
      toast("success", "Site updated successfully.");
      setSite(data);
      setSomethingChanged(false);
    },
  });

  useEffect(() => {
    populateFormDataFromSite();
  }, [site]);

  function populateFormDataFromSite() {
    if (!site) return;

    const newFormData = (Object.keys(formData) as (keyof formDataType)[]).reduce((acc, key) => {
      acc[key] = (site as any)[key] || formData[key];
      return acc;
    }, {} as formDataType);

    setFormData(newFormData);
  }

  function cancelChanges() {
    populateFormDataFromSite();
    setSomethingChanged(false);
    setFieldErrors({});
  }

  async function handleSubmit() {
    const sitePayload = {
      ...site,
      ...formData,
    };

    delete sitePayload.industry_list;
    delete sitePayload.job_task_list;
    delete sitePayload.setup_complete;
    delete sitePayload.badge;
    delete sitePayload.updated_at;
    delete sitePayload.created_at;
    delete sitePayload.deleted_at;

    await updateSite({
      payload: sitePayload,
    });
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;

    // Clear any existing error for this field
    if (fieldErrors[name]) {
      setFieldErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }

    setSomethingChanged(true);

    setFormData({
      ...formData,
      [name]: value,
    });
  }
  return (
    <>
      <Typography level="h2" component="h1">
        Client Professional Team: {site?.name}
      </Typography>
      <Card>
        <Divider style={{ margin: 0 }} align="center">
          <span>Safety agent</span>
        </Divider>
        <div className={styles["responsive-grid-2"]}>
          <Input
            label={"Safety agent company name"}
            name={"client_safety_agent_company_name"}
            value={formData.client_safety_agent_company_name}
            onChange={handleInputChange}
          />
          <Input
            label={"Safety agent name"}
            name={"client_safety_agent_name"}
            value={formData.client_safety_agent_name}
            onChange={handleInputChange}
          />
        </div>
        <div className={styles["responsive-grid-2"]}>
          <div>
            <label className="block mb-2">Safety agent phone number</label>
            <PhoneInput
              defaultCountry="za"
              value={formData.client_safety_agent_phone}
              name={"client_safety_agent_phone"}
              onChange={e => {
                setFormData({
                  ...formData,
                  client_safety_agent_phone: e || "",
                });
              }}
              inputStyle={{
                width: "100%",
                height: "50px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                padding: "0.75rem",
                fontSize: "1rem",
                lineHeight: "1.5",
                backgroundColor: "#ffffff",
                outline: "none",
                transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  border: "1px solid #d1d5db",
                  borderRadius: "6px 0 0 6px",
                  backgroundColor: "#ffffff",
                  padding: "0.75rem 0.5rem",
                  height: "50px",
                },
              }}
              inputClassName="p-inputtext"
            />
          </div>

          <Input
            name={"client_safety_agent_email"}
            label={"Safety agent email address"}
            value={formData.client_safety_agent_email}
            onChange={handleInputChange}
            placeholder={"Example: john.doe@dsf.co.za"}
            type={"email"}
          />
        </div>
        <Divider style={{ margin: 0 }} align="center">
          <p>Architect</p>
        </Divider>
        <div className={styles["responsive-grid-2"]}>
          <Input
            label={"Architect company name"}
            name={"client_architect_company_name"}
            value={formData.client_architect_company_name}
            onChange={handleInputChange}
          />
          <Input
            label={"Architect name"}
            name={"client_architect_name"}
            value={formData.client_architect_name}
            onChange={handleInputChange}
          />
        </div>
        <div className={styles["responsive-grid-2"]}>
          <div>
            <label className="block mb-2">Architect phone number</label>
            <PhoneInput
              defaultCountry="za"
              value={formData.client_architect_phone}
              name={"client_architect_phone"}
              onChange={e => {
                setFormData({
                  ...formData,
                  client_architect_phone: e || "",
                });
              }}
              inputStyle={{
                width: "100%",
                height: "50px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                padding: "0.75rem",
                fontSize: "1rem",
                lineHeight: "1.5",
                backgroundColor: "#ffffff",
                outline: "none",
                transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  border: "1px solid #d1d5db",
                  borderRadius: "6px 0 0 6px",
                  backgroundColor: "#ffffff",
                  padding: "0.75rem 0.5rem",
                  height: "50px",
                },
              }}
              inputClassName="p-inputtext"
            />
          </div>

          <Input
            name={"client_architect_email"}
            label={"Architect email address"}
            value={formData.client_architect_email}
            onChange={handleInputChange}
            placeholder={"Example: john.doe@dsf.co.za"}
            type={"email"}
          />
        </div>
        <Divider style={{ margin: 0 }} align="center">
          <p>Structural engineer</p>
        </Divider>
        <div className={styles["responsive-grid-2"]}>
          <Input
            label={"Structural engineer company name"}
            name={"client_structural_engineer_company_name"}
            value={formData.client_structural_engineer_company_name}
            onChange={handleInputChange}
          />
          <Input
            label={"Structural engineer name"}
            name={"client_structural_engineer_name"}
            value={formData.client_structural_engineer_name}
            onChange={handleInputChange}
          />
        </div>
        <div className={styles["responsive-grid-2"]}>
          <div>
            <label className="block mb-2">Structural engineer phone number</label>
            <PhoneInput
              defaultCountry="za"
              value={formData.client_structural_engineer_phone}
              name={"client_structural_engineer_phone"}
              onChange={e => {
                setFormData({
                  ...formData,
                  client_structural_engineer_phone: e || "",
                });
              }}
              inputStyle={{
                width: "100%",
                height: "50px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                padding: "0.75rem",
                fontSize: "1rem",
                lineHeight: "1.5",
                backgroundColor: "#ffffff",
                outline: "none",
                transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  border: "1px solid #d1d5db",
                  borderRadius: "6px 0 0 6px",
                  backgroundColor: "#ffffff",
                  padding: "0.75rem 0.5rem",
                  height: "50px",
                },
              }}
              inputClassName="p-inputtext"
            />
          </div>

          <Input
            name={"client_structural_engineer_email"}
            label={"Structural engineer email address"}
            value={formData.client_structural_engineer_email}
            onChange={handleInputChange}
            placeholder={"Example: john.doe@dsf.co.za"}
            type={"email"}
          />
        </div>
      </Card>
      <ButtonPanel inModal={false}>
        <Button
          label={"Cancel"}
          outlined={true}
          disabled={!somethingChanged || updatingSite}
          onClick={cancelChanges}
        />
        <Button
          label={"Save"}
          loading={updatingSite}
          disabled={updatingSite || !somethingChanged}
          onClick={async () => {
            await handleSubmit();
          }}
        />
      </ButtonPanel>
    </>
  );
}
